import { ApolloQueryResult } from '@apollo/client';
import { IApolloConnectionEdge } from '@netfront/common-library';

import { GET_PDF_USAGE_QUERY } from './useGetPdfUsage.graphql';
import { getUpdatedPdfUsage } from './useGetPdfUsage.helpers';
import {
  IGetPdfUsageQueryGraphQLResponse,
  IGetPdfUsageQueryVariables,
  IHandleFetchMorePdfUsageParams,
  IHandleGetPdfUsageParams,
  IUseGetPdfUsage,
  IUseGetPdfUsageOptions,
} from './useGetPdfUsage.interfaces';

import { IPdfUsage } from '../../../interfaces';
import { useKanziLazyQuery } from '../../apollo';

const useGetPdfUsage = (options?: IUseGetPdfUsageOptions): IUseGetPdfUsage => {
  const { fetchPolicy, onCompleted, onError, query, token } = options ?? ({} as IUseGetPdfUsageOptions);

  const [executeGetPdfUsage, { fetchMore, loading: isLoading }] = useKanziLazyQuery<IGetPdfUsageQueryGraphQLResponse, IGetPdfUsageQueryVariables>({
    options: {
      fetchPolicy: fetchPolicy ?? 'cache-and-network',
      onCompleted: (data) => {
        if (!onCompleted) {
          return;
        }

        const {
          report: {
            getPdfUsages: { edges, pageInfo, totalCount },
          },
        } = data;

        const updatedEdges = edges.map((edge) => {
          const { node } = edge;

          const updatedEdge: IApolloConnectionEdge<IPdfUsage> = {
            ...edge,
            node: getUpdatedPdfUsage(node),
          };

          return updatedEdge;
        });

        onCompleted({
          edges: updatedEdges,
          pageInfo,
          totalCount,
        });
      },
      onError,
    },
    query: query ?? GET_PDF_USAGE_QUERY,
    token,
  });

  const handleFetchMorePdfUsage = ({
      after,
      filter,
      first,
      from,
      ianaTimeZone,
      projectId,
      to,
    }: IHandleFetchMorePdfUsageParams): Promise<ApolloQueryResult<IGetPdfUsageQueryGraphQLResponse>> => {
      return fetchMore({
        updateQuery: (
          previousQueryResult,
          {
            fetchMoreResult: {
              report: { getPdfUsages: newlyFetchedPdfUsageConnection },
            },
          },
        ) => {
          const { edges: newlyFetchedEdges } = newlyFetchedPdfUsageConnection;
  
          if (!newlyFetchedEdges.length) {
            return previousQueryResult;
          }
  
          const {
            report: { getPdfUsages: previouslyFetchedPdfUsageConnection },
          } = previousQueryResult;
  
          const { edges: previouslyFetchedEdges } = previouslyFetchedPdfUsageConnection;
  
          return {
            ...previousQueryResult,
            report: {
              ...previouslyFetchedPdfUsageConnection,
              getPdfUsages: {
                ...newlyFetchedPdfUsageConnection,
                edges: [...previouslyFetchedEdges, ...newlyFetchedEdges],
              },
            },
          };
        },
        variables: {
          after,
          filter,
          first,
          from,
          ianaTimeZone,
          projectId,
          to,
        },
      });
    };

  const handleGetPdfUsage = ({ after, filter, first, from, ianaTimeZone, projectId, to }: IHandleGetPdfUsageParams) => {
    executeGetPdfUsage({
      variables: {
        after,
        filter,
        first,
        from,
        ianaTimeZone,
        projectId,
        to,
      },
    });
  };

  return {
    handleFetchMorePdfUsage,
    handleGetPdfUsage,
    isLoading,
  };
};

export { useGetPdfUsage };
